import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import { createContext, useContext } from 'react'

export type ToastTone = 'success' | 'error' | 'info'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  /** Turns the toast into a one-shot undo prompt. */
  action?: ToastAction
}

export interface Toast {
  id: number
  tone: ToastTone
  message: string
  action?: ToastAction | undefined
}

export interface ToastApi {
  success: (message: string, options?: ToastOptions) => void
  error: (message: string) => void
  info: (message: string, options?: ToastOptions) => void
}

/**
 * Context and hook live apart from the provider so that files export only one
 * kind of thing, which keeps fast refresh working during development.
 */
export const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider')
  }
  return context
}

export const toastToneStyles: Record<
  ToastTone,
  { wrap: string; icon: React.ReactNode }
> = {
  success: {
    wrap: 'border-positive/30 bg-positive-soft text-positive',
    icon: <CheckCircle2 size={16} />,
  },
  error: {
    wrap: 'border-critical/30 bg-critical-soft text-critical',
    icon: <AlertTriangle size={16} />,
  },
  info: { wrap: 'border-line-strong bg-surface text-ink', icon: <Info size={16} /> },
}